import type { ITicketTour } from '@/shared/api/types/ticket-types'
import type { ITicketFeature } from '@/shared/api/types/ticket-feature-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/libs/cn'
import { resolveTicketFeatureName } from '@/shared/utils/ticket-feature-name'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'

interface TicketTourFeaturesCardProps {
  tour: ITicketTour
  features: ITicketFeature[]
  languageCode: string
  saving: boolean
  onSave: (featureIds: string[]) => void
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id))
}

export function TicketTourFeaturesCard({
  tour,
  features,
  languageCode,
  saving,
  onSave,
}: TicketTourFeaturesCardProps) {
  const { t } = useTranslation()
  const savedIds = tour.features.map((feature) => feature.featureId)
  const [selectedIds, setSelectedIds] = useState<string[]>(savedIds)
  const tourTitle = tour.name?.trim() ?? tour.tourId
  const isDirty = !sameIds(selectedIds, savedIds)

  // Keep inactive features that are still bound to this tour so admins can see and remove them.
  const visibleFeatures = features.filter(
    (feature) => feature.isActive || savedIds.includes(feature.id),
  )

  function toggle(featureId: string, checked: boolean) {
    setSelectedIds((current) =>
      checked
        ? Array.from(new Set([...current, featureId]))
        : current.filter((id) => id !== featureId),
    )
  }

  return (
    <section className="rounded-xl border border-zinc-200 bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-zinc-100 px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-zinc-900">{tourTitle}</h3>
          <p className="text-xs text-zinc-500">
            {t('ticket.selectedOfTotal', {
              selected: selectedIds.length,
              total: visibleFeatures.length,
              defaultValue: '{{selected}} of {{total}} features enabled',
            })}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!isDirty || saving}
            onClick={() => setSelectedIds(savedIds)}
          >
            {t('common.reset', { defaultValue: 'Reset' })}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!isDirty || saving}
            onClick={() => onSave(selectedIds)}
          >
            {t('common.save', { defaultValue: 'Save' })}
          </Button>
        </div>
      </header>

      {visibleFeatures.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-zinc-500">
          {t('ticket.noFeatures', { defaultValue: 'No ticket features available.' })}
        </p>
      ) : (
        <div className="divide-y divide-zinc-100">
          {visibleFeatures.map((feature) => {
            const checked = selectedIds.includes(feature.id)
            const inputId = `${tour.tourId}-${feature.id}`
            return (
              <label
                key={feature.id}
                htmlFor={inputId}
                className={cn(
                  'flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-zinc-50',
                  checked && 'bg-blue-50/40 hover:bg-blue-50/60',
                )}
              >
                <Checkbox
                  id={inputId}
                  checked={checked}
                  disabled={saving}
                  onCheckedChange={(value) => toggle(feature.id, value === true)}
                />
                <Badge variant="secondary" className="w-fit font-mono text-[10px]">
                  {feature.featureKey}
                </Badge>
                <span className="text-sm font-medium text-zinc-900">
                  {resolveTicketFeatureName(feature, languageCode)}
                </span>
              </label>
            )
          })}
        </div>
      )}
    </section>
  )
}
